// src/features/form/AnalyticsTab.tsx
// GetFormAnalytics — totals, completion rate, trend, per-field stats.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import { formApi } from '@core/api';
import type { Form, FormAnalytics } from '@core/types/form';
import { errMessage } from './helpers';

export interface AnalyticsTabProps {
  form: Form;
}

export function AnalyticsTab({ form }: AnalyticsTabProps) {
  const { t } = useI18n();
  const formId = form.entity?.id ?? '';
  const [data, setData] = useState<FormAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    formApi
      .GetFormAnalytics(formId)
      .then((res) => setData(res.analytics ?? null))
      .catch((e) => setError(errMessage(e, t('form.analytics.loadError'))))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  if (isLoading) return <div className="h-32 animate-pulse rounded-md bg-muted" aria-busy="true" />;
  if (error) return <Alert variant="error">{error}</Alert>;
  if (!data || data.total_submissions === 0) {
    return (
      <div className="rounded-md border border-dashed border-border p-8 text-center">
        <p className="text-sm text-muted-foreground">{t('form.analytics.empty')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-md border border-border p-4">
          <p className="text-xs text-muted-foreground">{t('form.analytics.totalSubmissions')}</p>
          <p className="mt-1 text-2xl font-semibold text-foreground">{data.total_submissions}</p>
        </div>
        <div className="rounded-md border border-border p-4">
          <p className="text-xs text-muted-foreground">{t('form.analytics.completionRate')}</p>
          <p className="mt-1 text-2xl font-semibold text-foreground">{Math.round(data.completion_rate * 100)}%</p>
        </div>
        <div className="rounded-md border border-border p-4">
          <p className="text-xs text-muted-foreground">{t('form.analytics.avgTime')}</p>
          <p className="mt-1 text-2xl font-semibold text-foreground">{Math.round(data.avg_completion_time_sec)}s</p>
        </div>
      </div>

      {data.field_stats.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">{t('form.analytics.fieldStats')}</p>
          {data.field_stats.map((fs) => (
            <div key={fs.field_id} className="rounded-md border border-border p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground">{fs.label}</span>
                <span className="text-xs text-muted-foreground">
                  {t('form.analytics.responsesSkips', { responses: fs.response_count, skips: fs.skip_count })}
                </span>
              </div>
              {fs.value_distribution.length > 0 && (
                <div className="mt-2 space-y-1">
                  {fs.value_distribution.map((vc) => (
                    <div key={vc.value} className="flex items-center gap-2 text-xs">
                      <span className="w-24 shrink-0 truncate text-muted-foreground">{vc.value}</span>
                      <div className="h-1.5 flex-1 rounded-full bg-muted">
                        <div
                          className="h-1.5 rounded-full bg-primary"
                          style={{ width: `${fs.response_count ? (vc.count / fs.response_count) * 100 : 0}%` }}
                        />
                      </div>
                      <span className="w-8 shrink-0 text-right text-muted-foreground">{vc.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
